import { useCallback, useEffect, useRef, useState } from "react";
import {useHistory} from "react-router-dom";
import {
    ExpertiseStep, StepAction, StepsResponse,
    acceptRequest, addContract, cancelDeal, closeDeal, confirmDeal, declineRequest, getRequestSteps,
    removeContract, setDealValue, skipContract,
} from "../../../../../../services/expertiseAdminService";
import { handleApiError } from "../../../../../../utils/handleApiError";
import { showErrorAlert } from "../../../../../../helpers/alert";

/**
 * Card "STEPS" del panel derecho (Figma 4731:130527 experto / 4731:130394 comprador).
 *
 * Las dos perspectivas se pintan con ESTE mismo componente. No hay `if (isExpert)` repartido por el
 * marcado: el backend ya devuelve, por paso, el titulo correcto y la lista de `actions` que esa
 * persona puede ejecutar. Duplicar aqui las reglas de permisos habria dejado dos verdades que se
 * desincronizan a la primera regla de negocio que cambie.
 */
type Props = {
    requestId?: number;
    /** Se llama tras cualquier accion para que el hilo recargue sus mensajes de sistema. */
    onChanged?: () => void;
    /** El paso 1 delega en el modal de reagenda que ya vive en ExpertiseContent. */
    onReschedule?: () => void;
    /** Avisa al contenedor mientras se trae el timeline, para que muestre el overlay. */
    onLoadingChange?: (loading: boolean) => void;
    /**
     * Contador que el padre incrementa cuando ocurrio una accion externa (Confirmar/Reject en
     * ExpertiseRequestCard). Sin esto el timeline conservaria `actions: ['accept','reject']` de la
     * carga previa y el usuario podia dispararlas otra vez, obteniendo un 400 "not pending".
     */
    refreshKey?: number;
}

const Caret = ({ open }: { open: boolean }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none"
        className={`expertise-steps__caret${open ? '' : ' expertise-steps__caret--closed'}`}
        aria-hidden="true"
    >
        <path d="M3.75 9.375L7.5 5.625L11.25 9.375" stroke="#151515" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/** Paso resuelto: circulo con palomita y borde negro. */
const TickIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z" stroke="#151515" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7.75 12L10.58 14.83L16.25 9.17004" stroke="#151515" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/** Paso aun no resuelto: hoja de documento en gris. */
const PendingIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M14.6667 6.66667V10C14.6667 13.3333 13.3333 14.6667 10 14.6667H6C2.66667 14.6667 1.33333 13.3333 1.33333 10V6C1.33333 2.66667 2.66667 1.33333 6 1.33333H9.33333" stroke="#808080" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14.6667 6.66667H12C10 6.66667 9.33333 6 9.33333 4V1.33333L14.6667 6.66667Z" stroke="#808080" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CalendarIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M8 2V5" stroke="#151515" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 2V5" stroke="#151515" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3.5 9.08997H20.5" stroke="#151515" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z" stroke="#151515" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15.6947 13.7H15.7037" stroke="#151515" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15.6947 16.7H15.7037" stroke="#151515" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M11.9955 13.7H12.0045" stroke="#151515" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M11.9955 16.7H12.0045" stroke="#151515" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.29431 13.7H8.30329" stroke="#151515" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.29431 16.7H8.30329" stroke="#151515" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ArrowIcon = () => (
    <svg width="13" height="4" viewBox="0 0 13.3502 3.88356" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M8.5 0.379406L12 3.38356H0" stroke="white" />
    </svg>
);

const CloseIcon = () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M1 1L9 9M9 1L1 9" stroke="#808080" strokeLinecap="round" />
    </svg>
);

const formatDate = (value?: string | null) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
}

const formatDateTime = (value?: string | null) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    const date = d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toLowerCase();
    return `${date} - ${time}`;
}

const money = (amount?: string | null, currency?: string | null) => {
    if (amount === null || amount === undefined || amount === '') return null;
    const n = Number(amount);
    if (isNaN(n)) return String(amount);
    // Los montos llegan como string (DecimalField); se muestran sin decimales cuando son enteros.
    const body = n % 1 === 0 ? n.toLocaleString('en-US') : n.toLocaleString('en-US', { minimumFractionDigits: 2 });
    return currency && currency.toUpperCase() !== 'USD' ? `${body} ${currency.toUpperCase()}` : `$${body}`;
}

/** Etiqueta gris + valor negro, el par que el diseno repite en el paso 1. */
const Field = ({ label, value }: { label: string; value?: string | null }) => {
    if (!value) return null;
    return (
        <div className="expertise-steps__field">
            <span className="expertise-steps__field-label">{label}</span>
            <span className="expertise-steps__field-value">{value}</span>
        </div>
    );
}

export const ExpertiseSteps = ({ requestId, onChanged, onReschedule, onLoadingChange, refreshKey }: Props) => {
    const history = useHistory();
    const [open, setOpen] = useState(true);
    const [data, setData] = useState<StepsResponse | null>(null);
    const [busy, setBusy] = useState(false);
    const [amount, setAmount] = useState('');
    const fileInput = useRef<HTMLInputElement>(null);
    // El monto se re-sincroniza SOLO cuando cambia en el servidor; si no, escribir en el campo
    // dispararia el efecto y el cursor saltaria en cada tecla.
    const serverAmount = useRef<string | null>(null);

    // El callback se guarda en un ref para poder avisar del loading sin meterlo en las
    // dependencias del efecto: el padre lo pasa como funcion nueva en cada render y el efecto se
    // reejecutaria en bucle.
    const notifyLoading = useRef(onLoadingChange);
    notifyLoading.current = onLoadingChange;

    const load = useCallback(async () => {
        if (!requestId) return;
        try {
            setData(await getRequestSteps(requestId));
        } catch (error: any) {
            handleApiError(error);
        }
    }, [requestId]);

    useEffect(() => {
        let alive = true;
        // Se limpia ANTES de pedir. Si no, al saltar de un hilo a otro el timeline del chat
        // anterior se queda pintado hasta que responde el nuevo, y por un instante se ven pasos y
        // montos que son de otra conversacion.
        setData(null);
        serverAmount.current = null;
        setAmount('');
        notifyLoading.current?.(true);

        load().finally(() => {
            // Si el usuario ya cambio de chat otra vez, el efecto siguiente manda: este no debe
            // apagar un overlay que corresponde a la carga nueva.
            if (alive) notifyLoading.current?.(false);
        });

        return () => { alive = false };
    }, [load]);

    useEffect(() => {
        const deal = data?.steps.find(s => s.key === 'deal');
        const incoming = deal?.data?.dealValue ?? null;
        if (incoming !== serverAmount.current) {
            serverAmount.current = incoming;
            setAmount(incoming ? String(incoming) : '');
        }
    }, [data]);

    // Refresh empujado por el padre tras una accion externa (Confirmar en ExpertiseRequestCard,
    // reagenda). No limpia `data` para evitar el parpadeo del timeline entero: se re-hace fetch en
    // silencio y en cuanto llega el snapshot nuevo, los botones que quedaron habilitados por
    // `actions: ['accept','reject']` de la carga previa se desactivan solos.
    useEffect(() => {
        if (refreshKey === undefined) return;
        // Se ignora el primer render (refreshKey inicial ya lo dispara el efecto de arriba).
        if (refreshKey === 0) return;
        load();
    }, [refreshKey, load]);

    /**
     * Ejecuta una accion y adopta el timeline que devuelve, sin recargar.
     *
     * Devuelve si salio bien. El error se atrapa aqui para no repetir try/catch en cada boton, y
     * por eso hace falta el booleano: sin el, quien encadena algo despues (navegar al checkout)
     * lo haria igual aunque la accion hubiera fallado.
     */
    const run = async (fn: () => Promise<StepsResponse>, success?: string): Promise<boolean> => {
        if (busy) return false;
        try {
            setBusy(true);
            setData(await fn());
            if (success) showErrorAlert({ title: 'Done', message: success });
            onChanged?.();
            return true;
        } catch (error: any) {
            handleApiError(error);
            return false;
        } finally {
            setBusy(false);
        }
    }

    if (!requestId || !data) return null;

    const isBuyer = data.perspective === 'client';
    const can = (step: ExpertiseStep, action: StepAction) => step.actions.includes(action);

    const renderStepBody = (step: ExpertiseStep) => {
        switch (step.key) {
            case 'request':
                return (
                    <>
                        <p className="expertise-steps__lead">
                            {data.perspective === 'expert' ? 'Request from' : 'Request to'}{' '}
                            {step.data.counterparty || '—'}
                        </p>
                        <div className="expertise-steps__fields">
                            <Field label="Expertise" value={step.data.expertise} />
                            <Field
                                label="Intentional Deal Value"
                                value={money(step.data.intentionalDealValue, step.data.currency)}
                            />
                            <div className="expertise-steps__field">
                                <span className="expertise-steps__field-label">Schedule Date</span>
                                <span className="expertise-steps__field-value">
                                    {formatDateTime(step.data.scheduledOn) || '—'}
                                </span>
                                {/* Una reagenda viva se muestra aqui: es el dato que decide si la
                                    contraparte tiene algo que confirmar. */}
                                {step.data.proposedOn && (
                                    <span className="expertise-steps__field-hint">
                                        Proposed: {formatDateTime(step.data.proposedOn)}
                                        {step.data.proposedByMe ? ' (waiting for the other party)' : ''}
                                    </span>
                                )}
                                {can(step, 'reschedule') && (
                                    <button
                                        type="button"
                                        className="expertise-steps__link expertise-steps__link--icon"
                                        onClick={onReschedule}
                                    >
                                        <CalendarIcon />
                                        <span>Reschedule</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </>
                );

            case 'response': {
                // Armando 2026-09-10: preferir DISABLE a HIDE una vez que la solicitud ya no esta
                // pendiente. Antes ocultabamos los botones con `can(...)`, pero eso creaba una
                // desincronia visible: si el usuario aceptaba desde ExpertiseRequestCard (el chat
                // grande de arriba), el timeline seguia con `actions: ['accept','reject']` de la
                // carga previa y permitia un segundo click que reventaba en 400 "not pending".
                //
                // Ahora renderamos siempre los botones y los deshabilitamos cuando (a) el back ya
                // no permite la accion o (b) el paso ya salio de `current` (es decir, el back lo
                // marco completed/declined). Con (b) cubrimos el caso en el que el timeline aun
                // trae actions no vacias pero el paso ya cerro localmente.
                const canAccept = can(step, 'accept');
                const canReject = can(step, 'reject');
                const responseSettled = step.state !== 'current';
                const disabledTitle = responseSettled ? 'This request has already been answered.' : undefined;
                return (
                    <>
                        <p className="expertise-steps__note">{step.data.note}</p>
                        <div className="expertise-steps__actions">
                            <button
                                type="button" className="expertise-steps__btn expertise-steps__btn--outline"
                                disabled={busy || !canReject || responseSettled}
                                title={disabledTitle}
                                // Aceptar y rechazar viven en el endpoint de la SOLICITUD, no en
                                // el del trato: el trato aun no existe en este punto. Por eso se
                                // relee el timeline en vez de adoptar la respuesta.
                                onClick={() => run(async () => {
                                    await declineRequest(requestId);
                                    return getRequestSteps(requestId);
                                }, 'The request was rejected.')}
                            >
                                Reject
                            </button>
                            <button
                                type="button" className="expertise-steps__btn expertise-steps__btn--solid"
                                disabled={busy || !canAccept || responseSettled}
                                title={disabledTitle}
                                onClick={() => run(async () => {
                                    await acceptRequest(requestId);
                                    return getRequestSteps(requestId);
                                }, 'The request was accepted.')}
                            >
                                <span>Accept</span><ArrowIcon />
                            </button>
                        </div>
                    </>
                );
            }

            case 'contract': {
                const contracts: any[] = step.data.contracts || [];
                return (
                    <>
                        {step.data.skipped && !contracts.length && (
                            <p className="expertise-steps__note">This step was skipped.</p>
                        )}

                        {contracts.map((c) => (
                            <div key={c.id} className="expertise-steps__file">
                                {c.url
                                    ? <a href={c.url} target="_blank" rel="noreferrer">{c.name}</a>
                                    : <span>{c.name}</span>}
                                {/* Solo el experto recibe canRemove=true; el comprador no ve la X. */}
                                {c.canRemove && (
                                    <button
                                        type="button" className="expertise-steps__file-remove"
                                        disabled={busy} aria-label={`Remove ${c.name}`}
                                        onClick={() => run(() => removeContract(requestId, c.id))}
                                    >
                                        <CloseIcon />
                                    </button>
                                )}
                            </div>
                        ))}

                        <input
                            type="file" ref={fileInput} className="d-none"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                e.target.value = '';
                                if (file) run(() => addContract(requestId, file), 'Contract attached.');
                            }}
                        />

                        {can(step, 'add_contract') && (
                            contracts.length ? (
                                // Con un documento ya adjunto la unica opcion es sumar otro: no hay
                                // SKIP ni forma de vaciar el paso.
                                <button
                                    type="button" className="expertise-steps__link" disabled={busy}
                                    onClick={() => fileInput.current?.click()}
                                >
                                    + Add additional
                                </button>
                            ) : (
                                <div className="expertise-steps__actions">
                                    {can(step, 'skip_contract') ? (
                                        <button
                                            type="button" className="expertise-steps__btn expertise-steps__btn--outline"
                                            disabled={busy}
                                            onClick={() => run(() => skipContract(requestId))}
                                        >
                                            Skip
                                        </button>
                                    ) : <span />}
                                    <button
                                        type="button" className="expertise-steps__btn expertise-steps__btn--solid"
                                        disabled={busy}
                                        onClick={() => fileInput.current?.click()}
                                    >
                                        <span>Add</span><ArrowIcon />
                                    </button>
                                </div>
                            )
                        )}
                    </>
                );
            }

            case 'deal': {
                const editable = can(step, 'set_deal_value');
                const value = money(step.data.dealValue, step.data.currency);
                return (
                    <>
                        <div className="expertise-steps__deal">
                            <span className="expertise-steps__deal-label">Deal value</span>
                            {editable ? (
                                <input
                                    type="number" min="0" step="0.01" className="expertise-steps__deal-input"
                                    value={amount} disabled={busy}
                                    onChange={(e) => setAmount(e.target.value)}
                                    onBlur={() => {
                                        // Se guarda al salir del campo, no en cada tecla. Cambiar el
                                        // monto resetea ambas confirmaciones en el backend.
                                        if (amount && amount !== serverAmount.current) {
                                            run(() => setDealValue(requestId, amount));
                                        }
                                    }}
                                />
                            ) : (
                                <span className="expertise-steps__deal-value">{value || '—'}</span>
                            )}
                            {step.data.note && (
                                <span className="expertise-steps__deal-hint">{step.data.note}</span>
                            )}
                        </div>

                        {(can(step, 'confirm_deal') || can(step, 'cancel_deal') || can(step, 'pay')) && (
                            <div className="expertise-steps__actions">
                                {can(step, 'cancel_deal') ? (
                                    <button
                                        type="button" className="expertise-steps__link" disabled={busy}
                                        onClick={() => run(() => cancelDeal(requestId), 'The deal was cancelled.')}
                                    >
                                        Cancelled
                                    </button>
                                ) : <span />}

                                {/* Para el COMPRADOR confirmar es pagar: acepta el monto del experto
                                    y sigue al checkout. Del lado del experto el boton dice Confirm,
                                    porque el no paga nada. */}
                                {can(step, 'confirm_deal') && (
                                    <button
                                        type="button" className="expertise-steps__btn expertise-steps__btn--solid"
                                        disabled={busy}
                                        onClick={() => {
                                            if (!isBuyer) {
                                                run(() => confirmDeal(requestId), 'Confirmed.');
                                                return;
                                            }
                                            run(() => confirmDeal(requestId)).then((ok) => {
                                                if (ok) history.push(`/expertise/checkout/${requestId}`);
                                            });
                                        }}
                                    >
                                        <span>{isBuyer ? 'Pay' : 'Confirm'}</span><ArrowIcon />
                                    </button>
                                )}

                                {/* Trato ya acordado y sin cobrar: retoma el checkout abandonado. */}
                                {can(step, 'pay') && (
                                    <button
                                        type="button" className="expertise-steps__btn expertise-steps__btn--solid"
                                        disabled={busy}
                                        onClick={() => history.push(`/expertise/checkout/${requestId}`)}
                                    >
                                        <span>Pay</span><ArrowIcon />
                                    </button>
                                )}
                            </div>
                        )}
                    </>
                );
            }

            case 'closed':
                return can(step, 'close_deal') ? (
                    <div className="expertise-steps__actions expertise-steps__actions--end">
                        <button
                            type="button" className="expertise-steps__btn expertise-steps__btn--solid"
                            disabled={busy}
                            onClick={() => run(() => closeDeal(requestId), 'The deal is closed.')}
                        >
                            <span>Close</span><ArrowIcon />
                        </button>
                    </div>
                ) : null;

            default:
                return null;
        }
    }

    return (
        <div className="expertise-steps">
            <button
                type="button" className="expertise-steps__header"
                onClick={() => setOpen(p => !p)} aria-expanded={open}
            >
                <span className="expertise-steps__title">Steps</span>
                <Caret open={open} />
            </button>

            {open && (
                <>
                    <div className="expertise-steps__list">
                        {data.steps.map((step, i) => (
                            <div
                                key={step.key}
                                className={`expertise-steps__step expertise-steps__step--${step.state}`}
                            >
                                <div className="expertise-steps__rail">
                                    <span className="expertise-steps__bullet">
                                        {step.state === 'completed' ? <TickIcon /> : <PendingIcon />}
                                    </span>
                                    {/* El ultimo paso no lleva linea: no hay nada debajo que unir. */}
                                    {i < data.steps.length - 1 && <span className="expertise-steps__line" />}
                                </div>

                                <div className="expertise-steps__content">
                                    <div className="expertise-steps__heading">
                                        <span className="expertise-steps__step-title">
                                            Step {step.index} - {step.title}
                                        </span>
                                        {step.completedAt && (
                                            <span className="expertise-steps__completed">
                                                Completed: {formatDate(step.completedAt)}
                                            </span>
                                        )}
                                    </div>
                                    {renderStepBody(step)}
                                </div>
                            </div>
                        ))}
                    </div>

                    {data.cancellationTerms && (
                        <div className="expertise-steps__footer">
                            <span className="expertise-steps__link" title={data.cancellationTerms}>
                                Cancellation terms
                            </span>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default ExpertiseSteps;
